// react
import { useMemo, useState } from 'react';
import styled from 'styled-components';

// components
import { Option, Title } from '@/components/atoms';
import { Editor } from '@/components/organisms';
import { FloatingActions } from '@/components/molecules';

// editor
import { createFieldHelper } from '@/components/organisms/EditorFields/fieldHelper';

// models
import {
  CareCircle,
  CareCircleMemberInvite,
  FieldType,
} from '@karehero/models';

/**
 * CareCircleInvite is an editor for creating a new care profile.
 */
export const CareCircleInvite = ({
  careCircle,
  onInvite,
  onCancel,
  relationshipOptions,
}: CareCircleInviteProps) => {
  // state
  const [value, setValue] = useState<CareCircleMemberInvite>({
    careCircleId: careCircle?.id,
    email: '',
    careRecipientAccountRelationshipId: '',
    firstName: '',
  });

  // memos
  const fields = useMemo(() => {
    return createFieldHelper<CareCircleMemberInvite>({
      firstName: {
        label: 'First name',
        field: {
          type: FieldType.Text,
        },
      },
      email: {
        label: 'Email',
        field: {
          type: FieldType.Email,
        },
      },
      careRecipientAccountRelationshipId: {
        label: 'Relationship to Care Recipient',
        field: {
          type: FieldType.Select,
          options: relationshipOptions,
        },
      },
    });
  }, [relationshipOptions]);

  return (
    <Wrapper>
      <Header>
        <Title>Invite to Care Circle</Title>
        <Description>
          Once invited your care circle member can join conversations with your
          care expert, view key information, and access the library resources.
        </Description>
      </Header>
      <Editor
        fields={fields}
        value={value}
        onChange={setValue}
        isActionsDisabled={true}
        isWithArrows
      />
      <ButtonWrapper>
        <FloatingActions
          actions={[
            {
              label: 'Cancel',
              onPress: onCancel,
              variant: 'secondary',
            },
            {
              label: 'Send email invite',
              onPress: () =>
                onInvite({ ...value, careCircleId: careCircle?.id }),
            },
          ]}
        />
      </ButtonWrapper>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[12]};
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
`;

const Description = styled.div`
  font: ${(props) => props.theme.typography.heading['2xl']};
  color: ${(props) => props.theme.color.neutral[90]};
`;

const ButtonWrapper = styled.div`
  position: relative;
`;

interface CareCircleInviteProps {
  careCircle?: CareCircle;
  onInvite: (careCircleMemberInvite: CareCircleMemberInvite) => Promise<void>;
  onCancel: () => void;
  relationshipOptions: Option[];
}

CareCircleInvite.displayName = 'CareCircleInvite';

export default CareCircleInvite;
